import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import PaperDraggable from "../../../../../components/PaperDraggable";
import StepperIncluirRecb from "./Stepper";
import { FormProvider, useForm } from "react-hook-form";
import moment from "moment";
import { formatarMonetárioParaNúmero } from "../../../../../helpers/masks";
import { TParcelas } from "./Stepper/Step02/InfoStep02";
import { useMutation, useQueryClient } from "react-query";
import axiosClient from "../../../../../lib/axios";
import { enqueueSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../../../contexts/AuthContext";
import { useContext } from "react";

export default function DialogIncluirRecebimento({
    open,
    handleClose,
}: DialogIncluirRecebimentoProps) {

    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const { userPerfilComLoja } = useContext(AuthContext);
    const lojaUsuario = userPerfilComLoja?.loja

    //Querys e mutations
    const { mutate: cadastrarTitulo, isLoading: isLoadingIncluirTitulo } = useMutation({
        mutationKey: 'cadastrar-titulo',
        mutationFn: (inputs: any) => axiosClient.post("/financeiro/contas-receber/cadastrar-titulo-receber", inputs),
        onError: (error) => {
            setValue('isLoadingIncluirTitulo', false)
            console.error(error)
            enqueueSnackbar('Erro ao cadastrar título!', { variant: 'error' });
        },
        onSuccess: ({ data }) => {
            queryClient.invalidateQueries(["table-data-grid-financeiro/contas-receber/pesquisar-titulos-receber"])
            handleClose()
            enqueueSnackbar('Título cadastrado com sucesso!', { variant: 'success' });
            setValue('isLoadingIncluirTitulo', false)
            navigate(`/financeiro/contas-receber/listar-todas?detalhe-titulo-receber=${data}`)
        }
    })

    //Funções manipuladoras
    const handleNextStep = () => {
        const stepper = getValues('stepper')
        setValue('stepper', stepper + 1)
    }

    const handleBackStep = () => {
        const stepper = getValues('stepper')
        setValue('stepper', stepper - 1)
    }

    const onSubmit = (data: any) => {
        setValue('isLoadingIncluirTitulo', true)

        const params = {
            //STEP01
            tipo_publico: data.identificacao.publico_dado.tipo_publico,
            codigo_publico: data.identificacao.publico_dado.codigo_publico,
            categoria_id: data.identificacao.categoria.id,
            ...(data.identificacao.fazenda_dado && {
                fazenda_apelido: data.identificacao.fazenda_dado.apelido
            }),
            tipo_documento_id: data.identificacao.tipo_documento.id,
            numero_documento: data.identificacao.numero_documento,
            //STEP02
            data_emissao: moment(data.valores.data_emissao).format("YYYY-MM-DD"),
            ...(data.valores.is_parcelado === "false" && { data_vencimento: moment(data.valores.data_vencimento).format("YYYY-MM-DD") }),
            valor_total: formatarMonetárioParaNúmero(data.valores.valor_total),
            is_parcelado: data.valores.is_parcelado,
            //STEP03
            vincular_publico: data.outrasInfos.vincular_outro_publico,
            local_cobranca_codigo: data.outrasInfos.local_cobranca.codigo,
            situacao_id: data.outrasInfos.situacao.id,
            departamento_id: data.outrasInfos.departamento.id,
            funcao_id: data.outrasInfos.funcoes.id,
            objeto: data.outrasInfos.objeto,
            historico: data.outrasInfos.historico,

            ...(data.outrasInfos.loja && {
                loja_id: data.outrasInfos.loja.id
            }),

            conta_contabil: data.outrasInfos.conta_contabil.codigo_contabil,
            ...(data.outrasInfos.conta_contabil.centro_custo && {
                conta_contabil_custo: data.outrasInfos.conta_contabil.centro_custo.id,
            }),

            contra_partida: data.outrasInfos.contra_partida.codigo_contabil,
            ...(data.outrasInfos.contra_partida.centro_custo && {
                contra_partida_custo: data.outrasInfos.contra_partida.centro_custo.id
            }),

            ...(data.outrasInfos.vincular_outro_publico === 'Sim' && {
                publico_vinculado_tipo_publico: data.outrasInfos.publico_vinculado.tipo_publico,
                publico_vinculado_codigo_publico: data.outrasInfos.publico_vinculado.codigo_publico

            }),

            ...(data.valores.is_parcelado === "true" && {
                parcelas: data.valores.parcelas.map((valor: TParcelas) => {
                    return {
                        valor: formatarMonetárioParaNúmero(valor.valor_parcela),
                        numero_parcela: valor.numero_parcela < 10 ? valor.numero_parcela : valor.numero_parcela,
                        data_vencimento: moment(valor.data_vencimento).format("YYYY-MM-DD")
                    }
                })
            })
        }

        cadastrarTitulo(params)
    }
    //

    //valores importantes
    const defaultValuesForm = {
        stepper: 0,
        isLoadingIncluirTitulo: isLoadingIncluirTitulo,
        identificacao: {
            publico_dado: '',
            fazenda_dado: '',
            tipo_documento: '',
            numero_documento: '',
            categoria: '',
        },
        valores: {
            data_vencimento: moment(),
            data_emissao: moment(),
            valor_total: "",
            is_parcelado: "false",
            parcelas: [{
                valor_parcela: "",
                numero_parcela: 1,
                data_vencimento: moment()
            }]
        },
        outrasInfos: {
            loja: lojaUsuario ? { id: lojaUsuario.id, nome: lojaUsuario.nome } : null,
            departamento: '',
            funcoes: '',
            local_cobranca: '',
            conta_contabil: null,
            contra_partida: null,
            situacao: '',
            vincular_outro_publico: 'Não',
            publico_vinculado: '',
            objeto: '',
            historico: ''
        },
        funcoesAux: {
            onNext: handleNextStep,
            onBack: handleBackStep,
            onCloseModal: handleClose,
            onSubmit: onSubmit
        },
    }

    const methods = useForm({ defaultValues: defaultValuesForm })
    const { getValues, setValue } = methods
    //


    return (
        <FormProvider {...methods}>
            <Dialog
                open={open}
                PaperComponent={PaperDraggable}
                aria-labelledby="draggable-dialog-title"
                fullWidth
                maxWidth={"md"}
                scroll="body"
            >
                <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                    <Typography variant="h6">Criar título a receber</Typography>
                </DialogTitle>

                <DialogContent>
                    <StepperIncluirRecb />
                </DialogContent>

                <DialogActions>
                    <Button
                        variant="text"
                        onClick={handleClose}
                    >
                        Fechar
                    </Button>
                </DialogActions>

            </Dialog>
        </FormProvider>
    )
}

interface DialogIncluirRecebimentoProps {
    open: boolean;
    handleClose: () => void;
}

